import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { en } from './en';
import { nl } from './nl';

export type Lang = 'en' | 'nl';
export type Dict = typeof en;
export type Key = keyof Dict;

const dicts: Record<Lang, Dict> = { en, nl };

interface I18n { lang: Lang; setLang: (l: Lang) => void; t: (k: Key, vars?: Record<string, string | number>) => string; s: (label: string) => string }
const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => { try { return (localStorage.getItem('ld.lang') as Lang) || 'en'; } catch { return 'en'; } });
  const setLang = useCallback((l: Lang) => { setLangState(l); try { localStorage.setItem('ld.lang', l); } catch { /* ignore */ } document.documentElement.lang = l; }, []);
  const value = useMemo<I18n>(() => ({
    lang, setLang,
    t: (k, vars) => {
      let s: string = dicts[lang][k] ?? en[k] ?? String(k);
      if (vars) for (const [name, v] of Object.entries(vars)) s = s.replaceAll(`{${name}}`, String(v));
      return s;
    },
    /** Localise a status/label string coming from the API (falls back to the input). */
    s: (label) => (lang === 'nl' ? nlStrings[label] ?? label : label),
  }), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside provider');
  return v;
}

/** Free-form strings from the API / status labels → Dutch. */
const nlStrings: Record<string, string> = {
  'All': 'Alles', 'Draft': 'Concept', 'Sent': 'Verstuurd', 'Overdue': 'Te laat', 'In dunning': 'In aanmaning', 'Paid': 'Betaald',
  'Reminder sent': 'Herinnering verstuurd', 'Demand 1': 'Aanmaning 1', 'Demand 2': 'Aanmaning 2', 'Demand letter 1': 'Aanmaning 1',
  'Demand letter 2': 'Aanmaning 2', 'Demand letter 2 (final)': 'Aanmaning 2 (laatste)', 'Payment reminder': 'Betalingsherinnering',
  'Hand over to collection': 'Overdragen aan incasso', 'Collection': 'Incasso', 'Cancelled': 'Geannuleerd', 'Corrected': 'Gecorrigeerd', 'The pattern is empty': 'Het patroon is leeg', 'The pattern is longer than 60 characters': 'Het patroon is langer dan 60 tekens',
  'The pattern needs a {seq} token for the running number': 'Het patroon heeft een {seq}-bouwsteen voor het volgnummer nodig', 'Only one {seq} token is allowed': 'Maar één {seq}-bouwsteen toegestaan', 'The pattern cannot contain spaces': 'Spaties zijn niet toegestaan', 'Imported': 'Geïmporteerd', 'Finalised': 'Definitief gemaakt', 'Created': 'Aangemaakt', 'Invoice': 'Factuur', 'Reminder': 'Herinnering',
  'Ticket reply': 'Ticketantwoord', 'Demand': 'Aanmaning', 'Portal invite': 'Portaaluitnodiging', 'Mail': 'Mail', 'Active': 'Actief', 'Paused': 'Gepauzeerd', 'Ended': 'Beëindigd',
  'Live': 'Live', 'New': 'Nieuw', 'In progress': 'In behandeling', 'Waiting on customer': 'Wacht op klant', 'Resolved': 'Opgelost', 'Closed': 'Gesloten',
  'High': 'Hoog', 'Normal': 'Normaal', 'Low': 'Laag', 'Unassigned': 'Niet toegewezen', 'Delivered': 'Afgeleverd', 'Opened': 'Geopend',
  'Failed': 'Mislukt', 'Bounced': 'Geweigerd', 'Queued': 'In wachtrij', 'Met': 'Gehaald', 'Due today': 'Vervalt vandaag',
  'Company': 'Bedrijf', 'Individual': 'Particulier', 'Customer': 'Klant', 'Supplier': 'Leverancier', 'On': 'Aan', 'Off': 'Uit',
  'Bank transfer': 'Naar bank overmaken', 'iDEAL': 'iDEAL', 'Direct debit': 'Automatische incasso', 'Not configured': 'Niet ingesteld',
  'Monthly': 'Maandelijks', 'Quarterly': 'Per kwartaal', 'Yearly': 'Jaarlijks', 'Company default': 'Bedrijfsstandaard',
  'NL 21%': 'NL 21%', 'NL 9%': 'NL 9%', 'NL 0%': 'NL 0%', 'ICP reverse charge': 'ICP verlegd', 'Outside EU': 'Buiten EU', 'Exempt': 'Vrijgesteld',
  'Primary recipient': 'Eerste ontvanger', 'Bcc': 'Bcc', 'Mails': 'Mailt', 'No mail': 'Geen mail',
  'Pending': 'Te controleren', 'Booked': 'Geboekt', 'Possible duplicate': 'Mogelijk dubbel', 'Ignored': 'Genegeerd', 'Unrecognised': 'Niet herkend',
};
